import { format, parseISO } from "date-fns";

import type { AdminTicketFiltersDto } from "@/api/types";
import { Button } from "@/components/Button/Button";
import { Checkbox } from "@/components/Checkbox/Checkbox";
import { CheckboxMultiSelect } from "@/components/CheckboxMultiSelect/CheckboxMultiSelect";
import { DatePicker } from "@/components/DatePicker/DatePicker";
import { Star01Icon } from "@/components/Icons/Star01Icon";
import { XIcon } from "@/components/Icons/XIcon";
import { Select } from "@/components/Select/Select";
import { useSessionUser } from "@/hooks/Network/useSessionUser";
import type { TicketFilterTypes } from "@/modules/tickets/constants";
import { ticketRatingValues, ticketVisibilityValues } from "@/modules/tickets/constants";
import { useTranslation } from "@/translations";

import type { TicketsFilters } from "../Loader";

interface TicketOverviewFilterProps {
  activeFilter: TicketsFilters;
  values: AdminTicketFiltersDto;
  isOpened: boolean;
  onClose: () => void;
  onUpdateFilter: (filter: keyof TicketsFilters, val: TicketFilterTypes) => void;
}

export function TicketOverviewFilter({
  activeFilter,
  isOpened,
  values,
  onClose,
  onUpdateFilter,
}: TicketOverviewFilterProps): React.ReactNode {
  const { t } = useTranslation();
  const sessionUser = useSessionUser();

  if (!isOpened) {
    return null;
  }

  // We're creating this object because the Select component checks the existence of items in the selected list by reference
  const unassignedUser = {
    id: "",
    fullName: t("page.tickets.header.filter.assignee.unassigned"),
  };

  return (
    <div className="w-full border-t border-grey-100">
      <div className="flex w-full flex-col items-start gap-2 py-4">
        <span className="text-caption-bold">{t("page.tickets.header.filter.label")}</span>
        <div className="flex flex-wrap items-center gap-2">
          <div className="min-w-52">
            <CheckboxMultiSelect
              items={[unassignedUser, ...values.assignees]}
              selected={[unassignedUser, ...values.assignees].filter((x) => activeFilter.AssigneeIds?.includes(x.id))}
              onChange={(u) =>
                onUpdateFilter(
                  "AssigneeIds",
                  u.map(({ id }) => id),
                )
              }
              renderValue={(value) => (
                <span className="block max-w-36 truncate pr-5">{value.map((x) => x.fullName).join(", ")}</span>
              )}
              placeholder={t("page.tickets.header.filter.assignee")}
              renderOption={(x) => x.fullName}
              keySelector={(x) => x.id}
            />
          </div>
          <div className="min-w-52">
            <CheckboxMultiSelect
              items={ticketRatingValues}
              selected={ticketRatingValues.filter((x) => activeFilter.Ratings?.includes(x))}
              onChange={(x) => onUpdateFilter("Ratings", x)}
              placeholder={t("page.tickets.header.filter.rating")}
              renderOption={(x) => {
                if (x === -1) {
                  return t("page.tickets.header.filter.rating.not-rated");
                } else {
                  return (
                    <div className="flex w-fit items-center gap-1 rounded-md bg-yellow-100 px-2 text-center">
                      <Star01Icon className="fill-yellow-400 text-yellow-500" />
                      <span className="text-caption-bold">{x}</span>
                    </div>
                  );
                }
              }}
              keySelector={(x) => x}
            />
          </div>
          <div className="min-w-52">
            <CheckboxMultiSelect
              className="max-h-min"
              items={values.statuses}
              selected={values.statuses.filter((x) => activeFilter.StatusIds?.includes(x.id))}
              onChange={(s) =>
                onUpdateFilter(
                  "StatusIds",
                  s.map(({ id }) => id),
                )
              }
              placeholder={t("page.tickets.header.filter.status")}
              renderOption={(x) => (
                <div
                  className="w-full rounded-md px-2 text-center"
                  style={{
                    textTransform: "uppercase",
                    color: x.color,
                    backgroundColor: `${x.color}1A`,
                  }}
                >
                  <span className="text-caption-bold">{x.name || "-"}</span>
                </div>
              )}
              keySelector={(x) => x.id}
            />
          </div>
          <div className="min-w-52">
            <CheckboxMultiSelect
              items={values.categories}
              selected={values.categories.filter((x) => activeFilter.CategoryIds?.includes(x.id))}
              onChange={(c) =>
                onUpdateFilter(
                  "CategoryIds",
                  c.map(({ id }) => id),
                )
              }
              renderValue={(value) => (
                <span className="block max-w-36 truncate pr-5">{value.map((x) => x.name).join(", ")}</span>
              )}
              placeholder={t("page.tickets.header.filter.category")}
              renderOption={(x) => x.name}
              keySelector={(x) => x.id}
            />
          </div>
          <div className="min-w-52">
            <Select
              items={ticketVisibilityValues}
              selected={activeFilter.Filter}
              emptyItem={t("page.tickets.header.filter.private-collective")}
              onChange={(x) => onUpdateFilter("Filter", x)}
              renderOption={(x) => {
                switch (x) {
                  case "private":
                    return t("page.tickets.header.filter.filter.private");
                  case "collective":
                    return t("page.tickets.header.filter.filter.collective");
                }
              }}
              keySelector={(x) => x}
            />
          </div>
          {sessionUser.project.type === "addressBased" && (
            <div className="min-w-52">
              <CheckboxMultiSelect
                items={values.addressTypes}
                selected={values.addressTypes.filter((x) => activeFilter.AddressTypes?.includes(x))}
                renderValue={(value) => <span className="block max-w-36 truncate pr-5">{value.join(", ")}</span>}
                onChange={(x) => onUpdateFilter("AddressTypes", x)}
                placeholder={t("page.tickets.header.filter.address-type")}
                renderOption={(x) => x}
                keySelector={(x) => x}
              />
            </div>
          )}
          {/* Closed date range */}
          <div className="min-w-52">
            <DatePicker
              placeholder={t("page.tickets.header.closed-from-date")}
              value={activeFilter.ClosedFrom ? parseISO(activeFilter.ClosedFrom) : ""}
              onChange={(value) => onUpdateFilter("ClosedFrom", value ? format(value, "yyyy-MM-dd") : undefined)}
              min={new Date("1753-01-01T12:00:00")}
              max={activeFilter.ClosedTo ? parseISO(activeFilter.ClosedTo) : new Date("9999-12-31T11:59:59")}
            />
          </div>
          <div className="min-w-52">
            <DatePicker
              placeholder={t("page.tickets.header.closed-to-date")}
              value={activeFilter.ClosedTo ? parseISO(activeFilter.ClosedTo) : ""}
              onChange={(value) => onUpdateFilter("ClosedTo", value ? format(value, "yyyy-MM-dd") : undefined)}
              min={activeFilter.ClosedFrom ? parseISO(activeFilter.ClosedFrom) : new Date("1753-01-01T12:00:00")}
              max={new Date("9999-12-31T11:59:59")}
            />
          </div>

          {/* Rated date range */}
          <div className="min-w-52">
            <DatePicker
              placeholder={t("page.tickets.header.rated-from-date")}
              value={activeFilter.RatedFrom ? parseISO(activeFilter.RatedFrom) : ""}
              onChange={(value) => onUpdateFilter("RatedFrom", value ? format(value, "yyyy-MM-dd") : undefined)}
              min={new Date("1753-01-01T12:00:00")}
              max={activeFilter.RatedTo ? parseISO(activeFilter.RatedTo) : new Date("9999-12-31T11:59:59")}
            />
          </div>
          <div className="min-w-52">
            <DatePicker
              placeholder={t("page.tickets.header.rated-to-date")}
              value={activeFilter.RatedTo ? parseISO(activeFilter.RatedTo) : ""}
              onChange={(value) => onUpdateFilter("RatedTo", value ? format(value, "yyyy-MM-dd") : undefined)}
              min={activeFilter.RatedFrom ? parseISO(activeFilter.RatedFrom) : new Date("1753-01-01T12:00:00")}
              max={new Date("9999-12-31T11:59:59")}
            />
          </div>
        </div>
        <Checkbox
          name="tickets_filter_unread"
          checked={activeFilter.UnreadOnly}
          onChange={(e) => onUpdateFilter("UnreadOnly", e.target.checked)}
          label={t("page.tickets.header.filter.unread")}
        />

        <div className="flex w-full justify-center">
          <Button styling="tertiary" onClick={onClose} icon={<XIcon />}>
            {t("page.tickets.header.button.filter-close")}
          </Button>
        </div>
      </div>
    </div>
  );
}
